import { isProductionEnvironment } from '../../ts/Context'
import { type ValidatedResponseData } from '~/../types'
import {
    safeGuardString,
    ERROR_DELIMITER,
    ERROR_STATUS_POSTFIX,
    ERROR_STATUS_PREFIX,
} from '../../utils'

type ApiHeader = {
	readonly 'Content-Type': string,
	readonly authorization: string,
}

export const API_URL_PRODUCTION = 'https://api.equipmentshare.com'
export const API_URL_STAGING = 'https://staging-api.equipmentshare.com'
export const GENERAL_ERROR_HEADING = 'Something went wrong...'
export const INTERNAL_SERVICE_ERROR_HEADING = 'There seems to be an Internal Service Error.'

export const getApiUrl = (): string => ( isProductionEnvironment() ? API_URL_PRODUCTION : API_URL_STAGING )

export const getHeaders = ( jwt: string | null ): ApiHeader => {
	return {
		'Content-Type': 'application/json',
		'authorization': `Bearer ${jwt}`,
	}
}

// forceJSON is when you have an endpoint that does not specify the right content-type and you want it to always see
// the return as JSON.
export const getAndValidateResponseData = async( response: Response, forceJSON = false ): Promise<ValidatedResponseData> => {
	const { ok, status, url } = response
	let data: { readonly message?: string } = {}

	// If there is no data often the api returns a blank text/html content type
	if ( forceJSON || response.headers.get( 'content-type' ) === 'application/json' ) {
		data = JSON.parse( safeGuardString( await response.text() ) )
	}

	if ( !ok ) {
		// Whitelist of not OK status, this should not throw errors.
		if ( status === 404 ) {
			return { response, data }
		}

		const humanError: string = status === 500 ? INTERNAL_SERVICE_ERROR_HEADING : GENERAL_ERROR_HEADING
		/*istanbul ignore next */ // Optional chaining is covered by unit tests
		const computerError: string | undefined = data?.message
		const statusOfError = `${ERROR_STATUS_PREFIX}${status}${ERROR_STATUS_POSTFIX}`

		if ( !computerError ) {
			// eslint-disable-next-line functional/no-throw-statement
			throw new Error( humanError )
		}

        // eslint-disable-next-line functional/no-throw-statement
		throw new Error( `${humanError}${statusOfError}${ERROR_DELIMITER}${computerError} (${url})` )
	}

	return { response, data }
}
